<template>
  <div>
    <menu-content-container
      :chip="'Carousel'"
      chip-secondary
      :border-bottom="false"
    >
    </menu-content-container>

    <menu-content-container>
      <t-input-number
        v-model="borderRadius"
        :label="$t('forms.button_border')"
      ></t-input-number>
    </menu-content-container>

    <menu-content-container :header="$t('forms.arrows')">
      <content-row>
        <t-switch
          v-model="visibleArrows"
          :label="$t('forms.visibility')"
        ></t-switch>
      </content-row>

      <content-row v-if="visibleArrows">
        <t-dropdown
          ref="dropdown"
          :label="$t('forms.social_style')"
          rounded-full
          style="flex: 1"
        >
          <template #selected-content>
            <div
              data-testid="selected-icon-set"
              style="display: flex; border-radius: 2px; padding: 2px 0px"
              :style="{
                backgroundColor: leftIcon.includes('black') ? '#cbcbcd' : '',
              }"
            >
              <img
                style="width: 24px; height: 24px; margin: 2px 4px"
                :src="leftIcon"
                alt="left icon"
              />
              <img
                style="width: 24px; height: 24px; margin: 2px 4px"
                :src="rightIcon"
                alt="right icon"
              />
            </div>
          </template>

          <template #content>
            <ul>
              <VueCustomScrollbar
                style="height: 200px"
                :options="{ wheelPropagation: false, suppressScrollX: true }"
              >
                <template v-for="(arrowTypes, i) in iconStyles" :key="i">
                  <template
                    v-for="arrowType in arrowTypes.styles"
                    :key="arrowType.key"
                  >
                    <template
                      v-for="styleType in arrowType.styles"
                      :key="styleType"
                    >
                      <template
                        v-for="color in ['black', 'grey', 'white']"
                        :key="color"
                      >
                        <li
                          style="height: 32px; justify-content: center"
                          :style="{
                            backgroundColor: color === 'black' ? '#cbcbcd' : '',
                          }"
                          @click="
                            selectIcon(
                              `${awsFolder}/arrows/${arrowTypes.key}/${arrowType.key}/${styleType}/${color}`
                            )
                          "
                        >
                          <img
                            style="width: 24px; height: 24px; margin: 2px 4px"
                            :src="`${awsFolder}/arrows/${arrowTypes.key}/${arrowType.key}/${styleType}/${color}-L.png`"
                            :alt="`${arrowTypes.key}/${arrowType.key}/${styleType}/${color}-L`"
                          />
                          <img
                            style="width: 24px; height: 24px; margin: 2px 4px"
                            :src="`${awsFolder}/arrows/${arrowTypes.key}/${arrowType.key}/${styleType}/${color}-R.png`"
                            :alt="`${arrowTypes.key}/${arrowType.key}/${styleType}/${color}-R`"
                          />
                        </li>
                      </template>
                    </template>
                  </template>
                </template>
              </VueCustomScrollbar>
            </ul>
          </template>
        </t-dropdown>
        <t-input-number
          v-model="iconWidth"
          :label="$t('forms.social_size')"
        ></t-input-number>
      </content-row>
    </menu-content-container>

    <menu-content-container :header="$t('forms.thumbnails')">
      <content-row>
        <t-switch v-model="visible" :label="$t('forms.visibility')"></t-switch>
      </content-row>

      <div v-if="visible">
        <content-row>
          <t-input-number
            v-model="thumbnailWidth"
            :max="160"
            :min="80"
            :label="$t('forms.width')"
          ></t-input-number>
          <t-input-number
            v-model="thumbnailborderRadius"
            :label="$t('forms.button_border')"
          ></t-input-number>
        </content-row>
        <content-row>
          <color-picker
            :default-color="hoverBorderColor"
            :label="$t('forms.hover_border_color')"
            @color-change="onHoverBorderColor"
          ></color-picker>
          <color-picker
            position="right"
            :default-color="selectBorderColor"
            :label="$t('forms.selected_border_color')"
            @color-change="onSelectBorderColor"
          ></color-picker>
        </content-row>
      </div>
    </menu-content-container>
    <menu-content-container border-full :padding-left-right="8">
      <CarouselItems
        v-if="blockChilren"
        :block-children="blockChilren"
      ></CarouselItems>
    </menu-content-container>

    <padding-form></padding-form>

    <menu-content-container border-full>
      <content-row>
        <hideOnMobile></hideOnMobile>
      </content-row>
      <content-row>
        <hideOnDesktop></hideOnDesktop>
      </content-row>
    </menu-content-container>
  </div>
</template>

<script lang="ts" setup>
  import hideOnMobile from "./components/hideOnMobile.vue";
  import hideOnDesktop from "./components/hideOnDesktop.vue";
  import CarouselItems from "./components/Carousel/CarouselItems.vue";
  import paddingForm from "./paddingForm.vue";
  import colorPicker from "./elements/colorPicker.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";

  import { TInputNumber, TSwitch, TDropdown } from "@topol/twig-ui";

  import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
  import { ref, computed } from "vue";
  import { TopolCarouslBlock } from "@/schemas/template/blocks/carouselBlockSchema";

  const iconStyles = ref([
    {
      key: "arrow-1",
      styles: [
        {
          key: "circle",
          styles: ["fill", "outline"],
        },
        {
          key: "simple",
          styles: ["outline"],
        },
        {
          key: "squared",
          styles: ["fill", "outline"],
        },
        {
          key: "squared-rounded",
          styles: ["fill", "outline"],
        },
      ],
    },
    {
      key: "arrow-2",
      styles: [
        {
          key: "circle",
          styles: ["fill", "outline"],
        },
        {
          key: "simple",
          styles: ["outline"],
        },
        {
          key: "squared",
          styles: ["fill", "outline"],
        },
        {
          key: "squared-rounded",
          styles: ["fill", "outline"],
        },
      ],
    },
    {
      key: "arrow-3",
      styles: [
        {
          key: "circle",
          styles: ["fill", "outline"],
        },
        {
          key: "simple",
          styles: ["outline"],
        },
        {
          key: "squared",
          styles: ["fill", "outline"],
        },
        {
          key: "squared-rounded",
          styles: ["fill", "outline"],
        },
      ],
    },
  ]);

  const iconWidthDef = ref(44);

  const dropdown = ref();

  const mjmlStore = useMjmlStore();

  const iconWidth = computed({
    get(): number {
      return mjmlStore.getActiveBlockAttrInt("icon-width");
    },
    set(val: number) {
      mjmlStore.updateBlockAttribute({
        prop: "icon-width",
        value: val + "px",
      });
    },
  });

  const visibleArrows = computed({
    get(): boolean {
      return mjmlStore.getActiveBlockAttrInt("icon-width") !== 0;
    },
    set(val: boolean) {
      if (val) {
        mjmlStore.updateBlockAttribute({
          prop: "icon-width",
          value: iconWidthDef.value + "px",
        });
      } else {
        iconWidthDef.value = iconWidth.value;
        mjmlStore.updateBlockAttribute({
          prop: "icon-width",
          value: "0px",
        });
      }
    },
  });

  const borderRadius = computed({
    get(): number {
      return mjmlStore.getActiveBlockAttrInt("border-radius")
        ? mjmlStore.getActiveBlockAttrInt("border-radius")
        : 6;
    },
    set(value: number) {
      mjmlStore.updateBlockAttribute({
        prop: "border-radius",
        value: value + "px",
      });
    },
  });

  const leftIcon = computed(() => {
    return mjmlStore.getActiveBlockAttrString("left-icon");
  });

  const rightIcon = computed(() => {
    return mjmlStore.getActiveBlockAttrString("right-icon");
  });

  const visible = computed({
    get(): boolean {
      return mjmlStore.getActiveBlockAttrString("thumbnails") !== "hidden";
    },
    set(value: boolean) {
      mjmlStore.updateBlockAttribute({
        prop: "thumbnails",
        value: value ? "visible" : "hidden",
      });
    },
  });

  const awsFolder = computed(() => {
    return "https://s3-eu-west-1.amazonaws.com/ecomail-assets/editor";
  });

  const thumbnailWidth = computed({
    get(): number {
      return mjmlStore.getActiveBlockAttrInt("tb-width")
        ? mjmlStore.getActiveBlockAttrInt("tb-width")
        : 110;
    },

    set(value: number) {
      mjmlStore.updateBlockAttribute({
        prop: "tb-width",
        value: value + "px",
      });
    },
  });

  const thumbnailborderRadius = computed({
    get(): number {
      return mjmlStore.getActiveBlockAttrInt("tb-border-radius")
        ? mjmlStore.getActiveBlockAttrInt("tb-border-radius")
        : 6;
    },

    set(value: number) {
      mjmlStore.updateBlockAttribute({
        prop: "tb-border-radius",
        value: value + "px",
      });
    },
  });

  const hoverBorderColor = computed(() => {
    return mjmlStore.getActiveBlockAttrString("tb-hover-border-color")
      ? mjmlStore.getActiveBlockAttrString("tb-hover-border-color")
      : "#fead0d";
  });

  const selectBorderColor = computed(() => {
    return mjmlStore.getActiveBlockAttrString("tb-selected-border-color")
      ? mjmlStore.getActiveBlockAttrString("tb-selected-border-color")
      : "#fead0d";
  });

  const blockChilren = computed(() => {
    return (mjmlStore.getActiveBlock as TopolCarouslBlock)?.children;
  });

  function onHoverBorderColor(color: string) {
    mjmlStore.updateBlockAttribute({
      prop: "tb-hover-border-color",
      value: color,
    });
  }

  function onSelectBorderColor(color: string) {
    mjmlStore.updateBlockAttribute({
      prop: "tb-selected-border-color",
      value: color,
    });
  }

  function selectIcon(iconType: string) {
    dropdown.value.opened = false;

    mjmlStore.updateBlockAttribute({
      prop: "left-icon",
      value: iconType + "-L.png",
    });

    mjmlStore.updateBlockAttribute({
      prop: "right-icon",
      value: iconType + "-R.png",
    });
  }
</script>
